import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import camelcaseKeys from 'camelcase-keys';
import ContactAPI from 'dashboard/api/contacts';

const CHANNEL_PRIORITY = {
  'Channel::Email': 1,
  'Channel::Whatsapp': 2,
  'Channel::Sms': 3,
  'Channel::TwilioSms': 4,
  'Channel::WebWidget': 5,
  'Channel::Api': 6,
};

export const generateLabelForContactableInboxesList = ({
  name,
  email,
  channelType,
  phoneNumber,
}) => {
  if (channelType === INBOX_TYPES.EMAIL) {
    return `${name} (${email})`;
  }
  if (
    channelType === INBOX_TYPES.TWILIO ||
    channelType === INBOX_TYPES.WHATSAPP
  ) {
    return phoneNumber ? `${name} (${phoneNumber})` : name;
  }
  return name;
};

const transformInbox = ({
  name,
  id,
  email,
  channelType,
  phoneNumber,
  medium,
  ...rest
}) => ({
  id,
  icon: getInboxIconByType(channelType, medium, 'line'),
  label: generateLabelForContactableInboxesList({
    name,
    email,
    channelType,
    phoneNumber,
  }),
  action: 'inbox',
  value: id,
  name,
  email,
  phoneNumber,
  channelType,
  medium,
  ...rest,
});

export const compareInboxes = (a, b) => {
  // Channels that have no priority defined should come at the end.
  const priorityA = CHANNEL_PRIORITY[a.channelType] || 999;
  const priorityB = CHANNEL_PRIORITY[b.channelType] || 999;

  if (priorityA !== priorityB) {
    return priorityA - priorityB;
  }

  const nameA = a.name || '';
  const nameB = b.name || '';
  return nameA.localeCompare(nameB);
};

export const buildContactableInboxesList = contactInboxes => {
  if (!contactInboxes) return [];

  return contactInboxes.map(transformInbox).sort(compareInboxes);
};

export const getCapitalizedNameFromEmail = email => {
  const name = email.match(/^([^@]*)@/)?.[1] || email.split('@')[0];
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export const processContactableInboxes = inboxes => {
  return inboxes.map(inbox => ({
    ...inbox.inbox,
    sourceId: inbox.sourceId,
  }));
};

export const mergeInboxDetails = (inboxesData, inboxesList = []) => {
  if (!inboxesData || !inboxesData.length) {
    return [];
  }

  return inboxesData.map(inboxData => {
    const matchingInbox =
      inboxesList.find(inbox => inbox.id === inboxData.id) || {};
    return {
      ...camelcaseKeys(matchingInbox, { deep: true }),
      ...inboxData,
    };
  });
};

export const prepareAttachmentPayload = (
  attachedFiles,
  directUploadsEnabled
) => {
  const files = [];
  attachedFiles.forEach(attachment => {
    if (directUploadsEnabled) {
      files.push(attachment.blobSignedId);
    } else {
      files.push(attachment.resource.file);
    }
  });
  return files;
};

export const prepareNewMessagePayload = ({
  targetInbox,
  selectedContact,
  message,
  subject,
  ccEmails,
  bccEmails,
  currentUser,
  attachedFiles = [],
  directUploadsEnabled = false,
}) => {
  const payload = {
    inboxId: targetInbox.id,
    sourceId: targetInbox.sourceId,
    contactId: Number(selectedContact.id),
    message: { content: message },
    assigneeId: currentUser.id,
  };

  if (attachedFiles?.length) {
    payload.files = prepareAttachmentPayload(
      attachedFiles,
      directUploadsEnabled
    );
  }

  if (subject) {
    payload.mailSubject = subject;
  }

  if (ccEmails) {
    payload.message.cc_emails = ccEmails;
  }

  if (bccEmails) {
    payload.message.bcc_emails = bccEmails;
  }

  return payload;
};

export const prepareWhatsAppMessagePayload = ({
  targetInbox,
  selectedContact,
  message,
  templateParams,
  currentUser,
}) => {
  return {
    inboxId: targetInbox.id,
    sourceId: targetInbox.sourceId,
    contactId: selectedContact.id,
    message: { content: message, template_params: templateParams },
    assigneeId: currentUser.id,
  };
};

export const generateContactQuery = ({ keys = ['email'], query }) => {
  return {
    payload: keys.map(key => {
      const filterPayload = {
        attribute_key: key,
        filter_operator: 'contains',
        values: [query],
        attribute_model: 'standard',
      };
      if (keys.findIndex(k => k === key) !== keys.length - 1) {
        filterPayload.query_operator = 'or';
      }
      return filterPayload;
    }),
  };
};

// API Calls
export const searchContacts = async ({ keys, query }) => {
  const {
    data: { payload },
  } = await ContactAPI.filter(
    undefined,
    'name',
    generateContactQuery({ keys, query })
  );
  const camelCasedPayload = camelcaseKeys(payload, { deep: true });
  // Filter contacts that have either phone_number or email
  const filteredPayload = camelCasedPayload?.filter(
    contact => contact.phoneNumber || contact.email
  );
  return filteredPayload || [];
};

// Normaliza um telefone digitado pra o formato E.164 (+DDI...). Mantém o '+' se
// já houver; senão, números locais brasileiros (DDD + número, 10-11 dígitos)
// recebem o DDI 55. Números com 12+ dígitos são assumidos como já tendo DDI.
export const normalizePhoneNumber = raw => {
  const trimmed = (raw || '').trim();
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return trimmed;
  if (trimmed.startsWith('+')) return `+${digits}`;
  if (digits.length >= 12) return `+${digits}`;
  return `+55${digits}`;
};

// Chave que IGNORA o nono dígito (celular BR), igual ao CRM de duplicados
// (duplicates_controller: LEFT(phone,5) || RIGHT(phone,8)). Faz "+5555991577388"
// (com 9) e "+555591577388" (sem 9) colapsarem na mesma chave "+555591577388".
export const phoneMatchKey = phone => {
  const e164 = normalizePhoneNumber(phone);
  const digits = e164.replace(/\D/g, '');
  // Só números BR com tamanho de DDD+celular usam a regra do 9º dígito.
  if (!e164.startsWith('+55') || digits.length < 12) return e164;
  return `+${digits.slice(0, 4)}${digits.slice(-8)}`;
};

// Busca contatos a partir do que o agente digitou. Telefone busca pelos ÚLTIMOS
// 8 dígitos (estáveis com/sem o 9) via endpoint /search (ILIKE), pra achar o
// contato independente do nono dígito; e-mail busca por e-mail.
export const searchContactsByInput = async rawInput => {
  // O evento pode chegar como string (ex.: 'ivan') OU como objeto
  // { keys, query } (ComposeNewConversationForm re-emite assim). Sem extrair o
  // .query, o objeto virava "[object Object]" e a busca voltava SEMPRE vazia.
  const raw =
    rawInput && typeof rawInput === 'object' && !Array.isArray(rawInput)
      ? rawInput.query
      : rawInput;
  const value = (raw || '').toString().trim();
  if (!value) return [];
  if (value.includes('@')) {
    return searchContacts({ keys: ['email'], query: value });
  }
  // Poucos dígitos = a pessoa está digitando um NOME (antes 'ivan' virava
  // string vazia e caía em 'Nenhum resultado'); muitos dígitos = telefone
  // (últimos 8, ignora máscara e nono dígito).
  const digits = value.replace(/\D/g, '');
  const query = digits.length >= 3 ? digits.slice(-8) : value;
  if (!query) return [];
  const {
    data: { payload },
  } = await ContactAPI.search(query);
  return camelcaseKeys(payload || [], { deep: true }).filter(
    contact => contact.phoneNumber || contact.email
  );
};

// Procura um contato que já exista com este telefone/e-mail — usado pra
// REAPROVEITAR (em vez de duplicar) ao iniciar conversa. Telefone casa pela
// chave que ignora o nono dígito.
const findExistingContact = async ({ query, isEmail }) => {
  try {
    const results = await searchContactsByInput(query);
    if (isEmail) {
      return results.find(c => c.email === query) || results[0] || null;
    }
    const wantKey = phoneMatchKey(query);
    return (
      results.find(
        c => c.phoneNumber && phoneMatchKey(c.phoneNumber) === wantKey
      ) || null
    );
  } catch (error) {
    // Fail-safe: se a busca falhar, segue pro fluxo de criação.
    return null;
  }
};

// Decide se a entrada é e-mail (tem '@') ou telefone. Antes só tratava como
// telefone quando começava com '+', então um número digitado sem o '+' virava
// "e-mail" inválido e a criação do contato falhava silenciosamente.
// Se o contato já existe (telefone/e-mail duplicado → 422), reaproveita o
// existente em vez de falhar, pra deixar iniciar a conversa com ele.
export const createNewContact = async input => {
  const trimmed = (input || '').trim();
  const isEmail = trimmed.includes('@');
  const query = isEmail ? trimmed : normalizePhoneNumber(trimmed);

  // Busca ANTES de criar: se o número/e-mail já é contato, reaproveita e nem
  // chega a fazer o POST que daria 422 (evita o erro no console).
  const existing = await findExistingContact({ query, isEmail });
  if (existing) return existing;

  const payload = isEmail
    ? { name: getCapitalizedNameFromEmail(trimmed), email: trimmed }
    : { name: query.replace(/^\+/, ''), phone_number: query };

  try {
    const {
      data: {
        payload: { contact: newContact },
      },
    } = await ContactAPI.create(payload);
    return camelcaseKeys(newContact, { deep: true });
  } catch (error) {
    // Fallback de segurança: corrida/edge em que a busca não pegou mas o
    // contato já existe (telefone duplicado).
    const dup = await findExistingContact({ query, isEmail });
    if (dup) return dup;
    throw error;
  }
};

export const fetchContactableInboxes = async contactId => {
  // Sem contactId (contato ainda não criado) não há o que buscar — e chamar a
  // API com `undefined` batia /contacts/undefined/contactable_inboxes → 404.
  if (!contactId) return [];

  const {
    data: { payload: inboxes = [] },
  } = await ContactAPI.getContactableInboxes(contactId);

  const convertInboxesToCamelKeys = camelcaseKeys(inboxes, { deep: true });

  return processContactableInboxes(convertInboxesToCamelKeys);
};
